import { useEffect, useState } from "react";
import { t } from "../i18n";
import { Link } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { ScanFace, Trash2, LogOut, ChevronRight, MapPin, Info } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Shell } from "../components/Shell";
import { PushSettings } from "../components/PushSettings";
import { DataSection } from "../components/DataSection";
import { fmtFechaHora } from "../lib/data";

const Section = ({ title, children }) => (
  <section className="surface p-4 space-y-3">
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</h2>
    {children}
  </section>
);

const EV_KEY = { login: "settings.ev.login", login_fallido: "settings.ev.login_fail", pin_fallido: "settings.ev.pin_fail", bloqueo: "settings.ev.lock", pin_cambiado: "settings.changePin", webauthn_registrado: "settings.ev.webauthn_register", exportar: "settings.ev.exportar", borrar_cuenta: "settings.ev.borrar_cuenta" };
const EVENTOS = new Proxy({}, { get: (_, k) => (EV_KEY[k] ? t(EV_KEY[k]) : undefined) });

export default function Ajustes() {
  const { user, setUser, logout, refreshUser } = useAuth();
  const [s, setS] = useState(user.settings);
  const [pinForm, setPinForm] = useState({ password: "", nuevo_pin: "" });
  const [creds, setCreds] = useState([]);
  const [log, setLog] = useState({ items: [], page: 1, total: 0 });
  const [showLog, setShowLog] = useState(false);

  useEffect(() => { api.get("/webauthn/credentials").then((r) => setCreds(r.data)).catch(() => {}); }, []);
  const loadLog = (page = 1) => api.get("/auth/access-log", { params: { page } }).then((r) => { setLog(r.data); setShowLog(true); }).catch(() => {});

  const saveSettings = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try {
      const { data } = await api.put("/auth/settings", patch);
      setS(data); setUser({ ...user, settings: data });
      toast.success(t("settings.saved"));
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const changePin = async (e) => {
    e.preventDefault();
    try { await api.post("/pin/change", pinForm); toast.success("PIN actualizado"); setPinForm({ password: "", nuevo_pin: "" }); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const registerBio = async () => {
    try {
      const { data: options } = await api.post("/webauthn/register/options");
      const credential = await startRegistration({ optionsJSON: options });
      await api.post("/webauthn/register/verify", { credential, device_name: navigator.platform || t("settings.thisDevice") });
      toast.success(t("settings.bioRegistered"));
      setCreds((await api.get("/webauthn/credentials")).data);
      await refreshUser();
    } catch (err) {
      toast.error(err?.name === "NotAllowedError" ? t("settings.bioCancelled") : err?.name ? t("settings.bioUnavailable", { e: err.name }) : errorMessage(err));
    }
  };

  const removeCred = async (cid) => {
    try { await api.delete(`/webauthn/credentials/${encodeURIComponent(cid)}`); setCreds((c) => c.filter((x) => x.credential_id !== cid)); await refreshUser(); toast.success(t("settings.deviceRemoved")); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <Shell title={t("settings.title")} fab={false}>
      <div className="space-y-4 fade-up" data-testid="settings-page">
        <Section title={t("settings.security")}>
          <div>
            <label className="label">{t("settings.autoLock")}</label>
            <div className="flex gap-2">
              {[30, 60, 120, 300].map((v) => (
                <button key={v} data-testid={`autolock-${v}`} onClick={() => saveSettings({ auto_bloqueo_segundos: v })} className={`chip ${s.auto_bloqueo_segundos === v ? "chip-on" : ""}`}>{v < 60 ? `${v} s` : `${v / 60} min`}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label">{t("settings.biometric")}</label>
            <button data-testid="webauthn-register-btn" onClick={registerBio} className="btn-ghost w-full flex items-center justify-center gap-2"><ScanFace size={18} className="text-[#d4af37]" /> {t("settings.registerBio")}</button>
            <ul className="mt-2 divide-y divide-border" data-testid="webauthn-devices">
              {creds.map((c) => (
                <li key={c.credential_id} className="flex items-center justify-between py-2 text-sm">
                  <span>{c.device_name} <span className="text-xs text-muted-foreground">· {fmtFechaHora(c.created_at)}</span></span>
                  <button data-testid="webauthn-remove-btn" onClick={() => removeCred(c.credential_id)} className="p-2 text-muted-foreground hover:text-[#f43f5e]"><Trash2 size={16} /></button>
                </li>
              ))}
              {creds.length === 0 && <li className="py-2 text-xs text-muted-foreground">{t("settings.noDevices")}</li>}
            </ul>
          </div>
          <form onSubmit={changePin} className="space-y-2" data-testid="pin-change-form">
            <label className="label">{t("settings.changePin")}</label>
            <input data-testid="pin-change-password" className="field" type="password" placeholder={t("settings.currentPassword")} autoComplete="current-password" required value={pinForm.password} onChange={(e) => setPinForm({ ...pinForm, password: e.target.value })} />
            <input data-testid="pin-change-new" className="field tracking-[0.4em]" type="password" inputMode="numeric" pattern="\d{6}" maxLength={6} placeholder={t("settings.newPin")} required value={pinForm.nuevo_pin} onChange={(e) => setPinForm({ ...pinForm, nuevo_pin: e.target.value })} />
            <button data-testid="pin-change-submit" className="btn-ghost w-full">{t("settings.updatePin")}</button>
          </form>
        </Section>

        <Section title={t("settings.notifications")}>
          <PushSettings />
        </Section>

        <Section title={t("settings.prefs")}>
          <div>
            <label className="label">{t("settings.baseCity")}</label>
            <input data-testid="settings-ciudad-base" className="field" defaultValue={s.ciudad_base || ""} onBlur={(e) => e.target.value !== (s.ciudad_base || "") && saveSettings({ ciudad_base: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("settings.timezone")}</label>
            <input data-testid="settings-zona-horaria" className="field" defaultValue={s.zona_horaria} onBlur={(e) => e.target.value !== s.zona_horaria && saveSettings({ zona_horaria: e.target.value })} />
          </div>
          <div>
            <label className="label">{t("settings.noContactDays")}</label>
            <input data-testid="settings-dias-alerta" className="field" type="number" min={1} max={365} defaultValue={s.dias_sin_contacto_alerta} onBlur={(e) => Number(e.target.value) !== s.dias_sin_contacto_alerta && saveSettings({ dias_sin_contacto_alerta: Number(e.target.value) })} />
          </div>
        </Section>

        <Section title={t("settings.activity")}>
          <button data-testid="access-log-btn" onClick={() => loadLog(1)} className="btn-ghost w-full flex items-center justify-between">{t("settings.accessLog")} <ChevronRight size={16} /></button>
          {showLog && (
            <div data-testid="access-log-list">
              <ul className="divide-y divide-border max-h-72 overflow-y-auto">
                {log.items.map((e) => (
                  <li key={e.id} className="py-2 text-sm flex justify-between gap-3">
                    <span>{EVENTOS[e.evento] || e.evento}<span className="block text-xs text-muted-foreground truncate max-w-[200px]">{e.ip || ""}</span></span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtFechaHora(e.created_at)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between mt-2 text-sm">
                <button data-testid="access-log-prev" disabled={log.page <= 1} onClick={() => loadLog(log.page - 1)} className="text-muted-foreground disabled:opacity-30">{t("settings.prev")}</button>
                <span className="text-xs text-muted-foreground">{t("settings.pageOf", { p: log.page, t: log.total })}</span>
                <button data-testid="access-log-next" disabled={log.page * 50 >= log.total} onClick={() => loadLog(log.page + 1)} className="text-muted-foreground disabled:opacity-30">{t("settings.next")}</button>
              </div>
            </div>
          )}
        </Section>

        <Section title={t("settings.copilotHistory")}>
          <p className="text-xs text-muted-foreground">{t("settings.copilotHistoryDesc")}</p>
          <button data-testid="ai-logs-clear-btn" onClick={async () => { try { const { data } = await api.delete("/ai-logs"); toast.success(t("settings.historyCleared", { n: data.eliminados })); } catch (err) { toast.error(errorMessage(err)); } }} className="btn-ghost w-full text-sm text-[#f43f5e]">{t("settings.clearHistory")}</button>
        </Section>

        <Section title={t("settings.data")}>
          <DataSection />
        </Section>

        <Section title={t("settings.explore")}>
          <Link to="/mapa" data-testid="settings-mapa-link" className="flex items-center justify-between py-2 text-sm"><span className="flex items-center gap-2"><MapPin size={16} className="text-[#d4af37]" /> {t("home.mapTitle")}</span><ChevronRight size={16} className="text-muted-foreground" /></Link>
          <Link to="/acerca" data-testid="settings-acerca-link" className="flex items-center justify-between py-2 text-sm border-t border-border"><span className="flex items-center gap-2"><Info size={16} className="text-[#d4af37]" /> {t("about.title")}</span><ChevronRight size={16} className="text-muted-foreground" /></Link>
        </Section>

        <button data-testid="settings-logout-btn" onClick={logout} className="btn-ghost w-full flex items-center justify-center gap-2"><LogOut size={16} /> {t("common.logout")}</button>
        <p className="text-center text-xs text-muted-foreground">{user.email}</p>
      </div>
    </Shell>
  );
}
